import StoreIcon from "@mui/icons-material/Store";
import SearchIcon from "@mui/icons-material/Search";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import SyncIcon from "@mui/icons-material/Sync";
import { ElementType } from "react";
import DatabaseIcon from "@mui/icons-material/Storage";
import { useTranslation } from "../../i18n/hooks";

type SelectionConfig = {
  title: string;
  description: string;
  icon: ElementType;
};

type PlaceSelectionViewProps = {
  selectedPlaces: string[];
  setSelectedPlaces: (selectedPlaces: string[]) => void;
};

// Map icons to their colors
const iconColorMap: Record<string, string> = {
  'Content Optimization': 'text-purple-600',
  'Visual Enhancement': 'text-green-600',
  'SEO & Discovery': 'text-blue-600',
  'Market Sync': 'text-orange-600',
  'Data Management': 'text-rose-600',
};

// Enhanced checkbox component with improved UI
const PlaceCheckbox = ({
  title,
  description,
  Icon,
  selected,
  setSelected,
}: {
  title: string;
  description: string;
  Icon: ElementType;
  selected: boolean;
  setSelected: (selected: boolean) => void;
}) => {
  return (
    <button
      onClick={() => setSelected(!selected)}
      className={`w-full p-4 rounded-lg border-2 transition-all duration-200 flex items-center gap-4 hover:bg-blue-50 ${
        selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
      }`}
    >
      <div className="flex-shrink-0">
        <Icon className={`w-8 h-8 ${selected ? 'text-blue-600' : iconColorMap[title] || 'text-gray-600'}`} />
      </div>
      <div className="text-left flex-grow min-w-0">
        <h3 className="font-semibold text-lg">{title}</h3>
        <p className="text-gray-600 text-sm">{description}</p>
      </div>
      <div className="flex-shrink-0 ml-4">
        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
          selected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
        }`}>
          {selected && (
            <div className="w-2 h-2 bg-white rounded-full" />
          )}
        </div>
      </div>
    </button>
  );
};

const PlaceSelectionView = ({
  selectedPlaces,
  setSelectedPlaces,
}: PlaceSelectionViewProps) => {
  const { t } = useTranslation();
  
  const placeSelectionConfig: SelectionConfig[] = [
    {
      title: t('placeSelection.content_optimization'),
      description: t('placeSelection.content_optimization_desc'),
      icon: AutoFixHighIcon,
    },
    {
      title: t('placeSelection.image_finder'),
      description: t('placeSelection.image_finder_desc'),
      icon: SyncIcon,
    },
    {
      title: t('placeSelection.visual_enhancement'),
      description: t('placeSelection.visual_enhancement_desc'),
      icon: ImageSearchIcon,
    },
    {
      title: t('placeSelection.seo_discovery'),
      description: t('placeSelection.seo_discovery_desc'),
      icon: SearchIcon,
    },
    {
      title: t('placeSelection.data_management'),
      description: t('placeSelection.data_management_desc'),
      icon: DatabaseIcon,
    },
  ];

  return (
    <article className="w-full">
      <div className="grid grid-cols-1 gap-4">
        {placeSelectionConfig.map((place, index) => (
          <PlaceCheckbox
            key={index}
            title={place.title}
            description={place.description}
            Icon={place.icon}
            selected={selectedPlaces.includes(place.title)}
            setSelected={(selected) => {
              if (selected) {
                setSelectedPlaces([...selectedPlaces, place.title]);
              } else {
                setSelectedPlaces(
                  selectedPlaces.filter(
                    (selectedPlace) => selectedPlace !== place.title
                  )
                );
              }
            }}
          />
        ))}
      </div>
    </article>
  );
};

export default PlaceSelectionView;